import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { TextInput, Button } from '@datahub/design-system'
import { get, post } from '../lib/api'
import type { AdminUser } from '../lib/types'

interface SetPasswordProps {
  mode: 'invite' | 'reset'
  onSignedIn: (admin: AdminUser) => void
}

/** Invitation acceptance (ADM-07) and password reset (ADM-09) share this panel. */
export function SetPassword({ mode, onSignedIn }: SetPasswordProps) {
  const { token } = useParams<{ token: string }>()
  const [email, setEmail] = useState<string | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [expired, setExpired] = useState(false)
  const [done, setDone] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (mode !== 'invite' || !token) return
    get<{ email: string; displayName: string | null }>(`/auth/invite/${token}`)
      .then((res) => { setEmail(res.email); setDisplayName(res.displayName ?? '') })
      .catch(() => setExpired(true))
  }, [mode, token])

  const tooShort = password.length > 0 && password.length < 12
  const mismatch = confirm.length > 0 && confirm !== password
  const canSubmit = password.length >= 12 && password === confirm && (mode === 'reset' || displayName.trim().length > 0)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      if (mode === 'invite') {
        const res = await post<{ admin: AdminUser }>(`/auth/invite/${token}/accept`, { displayName: displayName.trim(), password })
        onSignedIn(res.admin)
      } else {
        await post(`/auth/password/reset/${token}`, { password })
        setDone(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That link is no longer valid')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="h-full overflow-y-auto bg-background-main" style={{ padding: 48 }}>
      <div style={{ maxWidth: 420, margin: '0 auto' }}>
        <img src="/images/stears-logo-white.webp" alt="Stears" style={{ height: 24, width: 'auto', marginBottom: 32 }} />
        <h1 className="font-jakarta font-bold text-white" style={{ fontSize: 20, lineHeight: '24px', marginBottom: 8 }}>
          {mode === 'invite' ? 'Accept your invitation' : 'Choose a new password'}
        </h1>

        {expired ? (
          <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px' }}>
            This link has expired. Ask a Datahub admin to send you a new invitation.
          </p>
        ) : done ? (
          <p className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px' }}>
            Your password has been changed and every other session was signed out.{' '}
            <a href="/admin" className="text-text-hyperlink hover:underline">Sign in</a>.
          </p>
        ) : (
          <form className="flex flex-col" style={{ gap: 20, marginTop: 24 }} onSubmit={(e) => { e.preventDefault(); void submit() }}>
            {mode === 'invite' && (
              <>
                <TextInput label="Email" size="large" value={email ?? ''} onChange={() => {}} disabled />
                <TextInput label="Your name" size="large" value={displayName} onChange={setDisplayName} />
              </>
            )}
            <TextInput
              label="Password"
              size="large"
              inputType="password"
              value={password}
              onChange={setPassword}
              {...(tooShort ? { error: 'Use at least 12 characters' } : {})}
            />
            <TextInput
              label="Confirm password"
              size="large"
              inputType="password"
              value={confirm}
              onChange={setConfirm}
              {...(mismatch ? { error: 'The two passwords do not match' } : {})}
            />
            {error && <span className="font-mulish" style={{ fontSize: 10, color: '#F05042' }}>{error}</span>}
            <div style={{ paddingTop: 4 }}>
              <Button variant="filled-grey" size="medium" className="w-full" disabled={!canSubmit} loading={busy} onClick={() => void submit()}>
                {mode === 'invite' ? 'Set password and continue' : 'Change password'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
